import type { MessageKey } from '../i18n/messages';

export const ASSET_OWNER_PAYOUT_COUNTRIES = ['IL', 'US'] as const;
export type AssetOwnerPayoutCountry = (typeof ASSET_OWNER_PAYOUT_COUNTRIES)[number];

export const ASSET_OWNER_PAYOUT_TRANSFER_DAYS = [2, 10, 15] as const;
export type AssetOwnerPayoutTransferDay = (typeof ASSET_OWNER_PAYOUT_TRANSFER_DAYS)[number];

const TRANSFER_DAY_OPTION_KEYS = {
  2: 'register.assetOwnerPayout.transferDayOption.2',
  10: 'register.assetOwnerPayout.transferDayOption.10',
  15: 'register.assetOwnerPayout.transferDayOption.15',
} as const satisfies Record<AssetOwnerPayoutTransferDay, MessageKey>;

/** i18n key for “the 2nd / 10th / 15th of each month”. */
export function assetOwnerPayoutTransferDayOptionKey(
  day: number,
): MessageKey | null {
  if (day === 2 || day === 10 || day === 15) {
    return TRANSFER_DAY_OPTION_KEYS[day];
  }
  return null;
}

export const ASSET_OWNER_US_ACCOUNT_TYPES = ['checking', 'savings'] as const;
export type AssetOwnerUsAccountType = (typeof ASSET_OWNER_US_ACCOUNT_TYPES)[number];

export type AssetOwnerPayoutBankFields = {
  payout_country: AssetOwnerPayoutCountry;
  account_holder_name: string;
  bank_number: string;
  branch_number: string;
  account_number: string;
  routing_number: string;
  account_type: AssetOwnerUsAccountType | '';
  payout_transfer_day: AssetOwnerPayoutTransferDay | '';
};

export const EMPTY_ASSET_OWNER_PAYOUT_BANK = {
  account_holder_name: '',
  bank_number: '',
  branch_number: '',
  account_number: '',
  routing_number: '',
  account_type: '' as const,
  payout_transfer_day: '' as const,
};

export const ASSET_OWNER_REGISTER_COUNTRY_IDS = [1, 2] as const;

export function assetOwnerPayoutCountryFromCountryId(countryId: unknown): AssetOwnerPayoutCountry {
  const n =
    typeof countryId === 'number' && Number.isFinite(countryId)
      ? countryId
      : typeof countryId === 'string' && countryId.trim()
        ? Number(countryId.trim())
        : NaN;
  return n === 2 ? 'US' : 'IL';
}

export function filterAssetOwnerRegisterCountries<T extends { id: number }>(countries: T[]): T[] {
  const allowed = new Set<number>(ASSET_OWNER_REGISTER_COUNTRY_IDS);
  const filtered = countries.filter((country) => allowed.has(country.id));
  return filtered.length > 0 ? filtered : countries;
}

export function parseAssetOwnerPayoutCountry(value: unknown): AssetOwnerPayoutCountry {
  return value === 'US' ? 'US' : 'IL';
}

export function parseAssetOwnerUsAccountType(value: unknown): AssetOwnerUsAccountType | '' {
  if (value === '' || value === null || value === undefined) {
    return '';
  }
  return value === 'savings' ? 'savings' : value === 'checking' ? 'checking' : '';
}

export function parseAssetOwnerPayoutTransferDay(value: unknown): AssetOwnerPayoutTransferDay | '' {
  if (value === '' || value === null || value === undefined) {
    return '';
  }
  const n =
    typeof value === 'number' && Number.isFinite(value)
      ? value
      : typeof value === 'string' && value.trim()
        ? Number(value.trim())
        : NaN;
  if (n === 2 || n === 10 || n === 15) {
    return n;
  }
  return '';
}

export function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

const ASSET_OWNER_PAYOUT_FLAT_KEYS = [
  'payout_country',
  'account_holder_name',
  'bank_number',
  'branch_number',
  'account_number',
  'routing_number',
  'account_type',
  'payout_transfer_day',
] as const;

export const ASSET_OWNER_PAYOUT_FIELD_KEYS = ASSET_OWNER_PAYOUT_FLAT_KEYS;

/** Register POST nests payout fields under `bank_account`; the form keeps them flat in state. */
export function flattenAssetOwnerPayoutPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const bank = payload.bank_account;
  if (bank && typeof bank === 'object' && !Array.isArray(bank)) {
    return { ...payload, ...(bank as Record<string, unknown>) };
  }
  return payload;
}

function payoutValuePresent(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return true;
  }
  return false;
}

/** Merge payout fields from the record and nested `asset_owner` envelopes (GET /profile shapes). */
export function resolveAssetOwnerPayoutFlatPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...flattenAssetOwnerPayoutPayload(payload) };

  const nestedSources: Record<string, unknown>[] = [];
  for (const key of ['asset_owner', 'assetOwner'] as const) {
    const nested = payload[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      nestedSources.push(flattenAssetOwnerPayoutPayload(nested as Record<string, unknown>));
    }
  }

  for (const source of nestedSources) {
    for (const key of ASSET_OWNER_PAYOUT_FLAT_KEYS) {
      if (!payoutValuePresent(merged[key]) && payoutValuePresent(source[key])) {
        merged[key] = source[key];
      }
    }
  }

  if (!payoutValuePresent(merged.account_number) && payoutValuePresent(merged.account_number_masked)) {
    merged.account_number = pickTrimmedString(merged.account_number_masked);
  }

  return merged;
}

/** Payout-only fields for forms and PATCH /profile (never the full GET /profile record). */
export function pickAssetOwnerPayoutFields(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const source = resolveAssetOwnerPayoutFlatPayload(payload);
  const picked: Record<string, unknown> = {};
  for (const key of ASSET_OWNER_PAYOUT_FLAT_KEYS) {
    const value = source[key];
    if (value !== undefined && value !== null && (typeof value !== 'string' || value.trim())) {
      picked[key] = value;
    }
  }
  if (payload.country_id !== undefined && payload.country_id !== null && payload.country_id !== '') {
    picked.country_id = payload.country_id;
  }
  return picked;
}

function extractAccountNumberFromSource(source: Record<string, unknown>): string {
  const raw = pickTrimmedString(source.account_number);
  if (raw.includes('*')) {
    return raw;
  }
  const full = digitsOnly(raw);
  if (full) {
    return full;
  }
  const masked = pickTrimmedString(source.account_number_masked);
  return masked;
}

export function extractAssetOwnerPayoutFromPayload(
  payload: Record<string, unknown>,
): AssetOwnerPayoutBankFields {
  const source = resolveAssetOwnerPayoutFlatPayload(payload);
  const payoutCountry =
    source.payout_country === 'US' || source.payout_country === 'IL'
      ? source.payout_country
      : assetOwnerPayoutCountryFromCountryId(payload.country_id);

  return {
    payout_country: payoutCountry,
    account_holder_name: pickRawString(source.account_holder_name),
    bank_number: digitsOnly(pickTrimmedString(source.bank_number)),
    branch_number: digitsOnly(pickTrimmedString(source.branch_number)),
    account_number: extractAccountNumberFromSource(source),
    routing_number: digitsOnly(pickTrimmedString(source.routing_number)),
    account_type: parseAssetOwnerUsAccountType(source.account_type),
    payout_transfer_day: parseAssetOwnerPayoutTransferDay(source.payout_transfer_day),
  };
}

export function assetOwnerPayoutMandatoryKeys(
  payoutCountry: AssetOwnerPayoutCountry,
): readonly string[] {
  const shared = ['account_holder_name', 'account_number', 'payout_transfer_day'] as const;
  if (payoutCountry === 'US') {
    return [...shared, 'routing_number', 'account_type'];
  }
  return [...shared, 'bank_number', 'branch_number'];
}

export function normalizeAccountHolderName(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function validateAssetOwnerPayoutFields(
  payload: Record<string, unknown>,
): MessageKey | null {
  const fields = extractAssetOwnerPayoutFromPayload(payload);
  const accountHolderName = normalizeAccountHolderName(fields.account_holder_name);

  if (!accountHolderName) {
    return 'register.assetOwnerPayout.error.accountHolderRequired';
  }

  if (!fields.account_number) {
    return 'register.assetOwnerPayout.error.accountNumberRequired';
  }

  if (fields.payout_country === 'IL') {
    if (!/^\d{2}$/.test(fields.bank_number)) {
      return 'register.assetOwnerPayout.error.bankNumberIl';
    }
    if (!/^\d{3}$/.test(fields.branch_number)) {
      return 'register.assetOwnerPayout.error.branchNumberIl';
    }
    if (!/^\d{4,12}$/.test(fields.account_number)) {
      return 'register.assetOwnerPayout.error.accountNumberIl';
    }
  } else {
    if (!/^\d{9}$/.test(fields.routing_number)) {
      return 'register.assetOwnerPayout.error.routingNumberUs';
    }
    if (!/^\d{4,17}$/.test(fields.account_number)) {
      return 'register.assetOwnerPayout.error.accountNumberUs';
    }
    if (!ASSET_OWNER_US_ACCOUNT_TYPES.includes(fields.account_type as AssetOwnerUsAccountType)) {
      return 'register.assetOwnerPayout.error.accountTypeUs';
    }
  }

  if (
    fields.payout_transfer_day === '' ||
    !ASSET_OWNER_PAYOUT_TRANSFER_DAYS.includes(fields.payout_transfer_day)
  ) {
    return 'register.assetOwnerPayout.error.transferDayRequired';
  }
  return null;
}

/** Mask account number for display (e.g. profile GET). */
export function maskBankAccountNumber(accountNumber: string): string {
  const digits = digitsOnly(accountNumber);
  if (digits.length <= 4) {
    return digits;
  }
  return `****${digits.slice(-4)}`;
}

export function normalizeAssetOwnerPayoutInPostBody(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const fields = extractAssetOwnerPayoutFromPayload(payload);
  const payout_country = fields.payout_country;
  const account_holder_name = normalizeAccountHolderName(fields.account_holder_name);
  const bank_number = fields.bank_number;
  const branch_number = fields.branch_number;
  const account_number = fields.account_number;
  const payout_transfer_day = fields.payout_transfer_day;

  const rest = { ...payload };
  for (const key of ASSET_OWNER_PAYOUT_FLAT_KEYS) {
    delete rest[key];
  }
  delete rest.bank_account;

  const bank_account: Record<string, unknown> = {
    payout_country,
    account_holder_name,
    account_number,
    payout_transfer_day,
  };

  if (payout_country === 'IL') {
    bank_account.bank_number = bank_number;
    bank_account.branch_number = branch_number;
  } else {
    bank_account.routing_number = fields.routing_number;
    bank_account.account_type = fields.account_type;
  }

  return {
    ...rest,
    payout_transfer_day,
    bank_account,
  };
}

/** Minimal PATCH /profile body — only nested bank_account (Nest whitelist rejects GET /profile fields). */
export function buildAssetOwnerPayoutProfilePatchBody(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const normalized = normalizeAssetOwnerPayoutInPostBody(pickAssetOwnerPayoutFields(payload));
  const bank_account = normalized.bank_account;
  if (!bank_account || typeof bank_account !== 'object' || Array.isArray(bank_account)) {
    return {};
  }
  return { bank_account };
}

function pickRawString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function pickTrimmedString(value: unknown): string {
  return pickRawString(value).trim();
}
